import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const Route = createFileRoute("/process")({
  head: () => buildSeo({
    title: "SEO Process — From Discovery to Compounding Growth | Freelancer Tamal",
    description: "A transparent six-stage SEO process: discovery, audit, strategy, execution, measurement, and compounding growth. Built to ship results, not slide decks.",
    path: "/process",
    ogEyebrow: "Process",
    keywords: "SEO process, SEO methodology, SEO workflow, technical SEO audit process",
  }),
  component: Process,
});

const STAGES = [
  { n: "01", t: "Discovery", d: "Deep-dive call to understand your offer, ICP, sales motion, and revenue goals. No template questionnaires — just real conversation." },
  { n: "02", t: "Audit", d: "200-point technical, content, and link audit. I look at your stack, your competitors, and the SERPs you actually want to win." },
  { n: "03", t: "Strategy", d: "A prioritized 90-day roadmap with KPIs, page-level briefs, and a forecast of what good looks like at 30/60/90 days." },
  { n: "04", t: "Execution", d: "I either lead your team through the work or ship it end-to-end myself — technical fixes, content, internal links, and authority building." },
  { n: "05", t: "Measurement", d: "Weekly Loom updates, monthly dashboards pulling from GSC, GA4, and your CRM. You always know what's working and why." },
  { n: "06", t: "Compounding", d: "Quarterly strategy reviews to double down on winners, kill losers, and keep the channel compounding while competitors plateau." },
];

function Process() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Process", path: "/process" }]} />
      <h1 className="sr-only">My SEO Process</h1>
      <Section eyebrow="Process" title={<>How the work <span className="text-gradient">actually ships.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          No SEO theater. No 80-page audits that sit in a Drive folder. Six
          stages, transparent at every step, built to compound.
        </p>
      </Section>

      <div className="container-x grid md:grid-cols-2 gap-6 pb-24">
        {STAGES.map((s, i) => (
          <Reveal key={s.n} delay={(i % 2) * 0.1}>
            <div className="rounded-3xl border border-border bg-card p-10 h-full hover:border-primary/40 transition">
              <div className="text-xs uppercase tracking-[0.2em] text-primary">{s.n}</div>
              <h2 className="mt-4 font-display text-3xl">{s.t}</h2>
              <p className="mt-4 text-muted-foreground">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Section eyebrow="Next" title="Want to see what stage one looks like?">
        <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
          Book a discovery call <ArrowUpRight size={16} />
        </Link>
      </Section>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "The Freelancer Tamal SEO Process — From Discovery to Compounding Growth",
        description: "A transparent six-stage SEO process: discovery, audit, strategy, execution, measurement, and compounding growth.",
        totalTime: "P90D",
        step: STAGES.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.t,
          text: s.d,
          url: `${SITE.url}/process#stage-${s.n}`,
        })),
      }} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Process", path: "/process" }])} />
    </>
  );
}
